/* eslint-disable max-lines-per-function */
import React from "react";
import { Card, CardContent } from "@mui/material";
import { BuilderQuestion, readableIdentity } from "./form-builder-utils";
import { guessQuestionTypeByName } from "./question-type-specs";
import {
  FormQuestionVariant,
  getQuestionIdentityKey,
  QuestionGroupsBySection,
} from "./form-builder-shared";
import FormBuilderQuestionCardHeader from "./form-builder-question-card-header";
import FormBuilderQuestionCardFields from "./form-builder-question-card-fields";

type Props = {
  question: BuilderQuestion;
  existingQuestions: Record<string, string>;
  questionIdentityOptions: string[];
  contextSuggestions: string[];
  questionIdentityLabelMap: Record<string, string>;
  questionIdentityTypeMap: Record<string, string>;
  questionGroupsBySection: QuestionGroupsBySection;
  variants: FormQuestionVariant[];
  canEditIdentity: boolean;
  isExpanded: boolean;
  onToggleExpanded: (questionId: string) => void;
  onDeleteQuestion: (questionId: string, questionKey: string) => void;
  onUpdateQuestion: (
    questionId: string,
    update: (question: BuilderQuestion) => BuilderQuestion,
  ) => void;
  onApplyVariant: (
    questionId: string,
    variant: FormQuestionVariant | null,
    preserveIdentity?: boolean,
  ) => void;
};

const FormBuilderQuestionCard: React.FC<Props> = ({
  question,
  existingQuestions,
  questionIdentityOptions,
  contextSuggestions,
  questionIdentityLabelMap,
  questionIdentityTypeMap,
  questionGroupsBySection,
  variants,
  canEditIdentity,
  isExpanded,
  onToggleExpanded,
  onDeleteQuestion,
  onUpdateQuestion,
  onApplyVariant,
}) => {
  const questionType = existingQuestions[question.name] || guessQuestionTypeByName(question.name) || "unknown";
  const variantOptions = variants
    .filter((variant) => variant.questionName === question.name)
    .sort((a, b) => b.usedBy - a.usedBy || b.version - a.version);
  const questionKey = getQuestionIdentityKey(question);
  const questionSummary = question.title || readableIdentity(questionKey);
  const questionNameOptions = Object.keys(existingQuestions).sort((a, b) => a.localeCompare(b));

  return (
    <Card key={question.draftId} variant="outlined">
      <CardContent>
        <FormBuilderQuestionCardHeader
          question={question}
          questionKey={questionKey}
          questionSummary={questionSummary}
          questionType={questionType}
          isExpanded={isExpanded}
          onToggleExpanded={onToggleExpanded}
          onDeleteQuestion={onDeleteQuestion}
        />

        {isExpanded && (
          <FormBuilderQuestionCardFields
            question={question}
            questionType={questionType}
            questionKey={questionKey}
            questionNameOptions={questionNameOptions}
            existingQuestions={existingQuestions}
            contextSuggestions={contextSuggestions}
            variantOptions={variantOptions}
            questionIdentityOptions={questionIdentityOptions}
            questionIdentityLabelMap={questionIdentityLabelMap}
            questionIdentityTypeMap={questionIdentityTypeMap}
            questionGroupsBySection={questionGroupsBySection}
            canEditIdentity={canEditIdentity}
            onUpdateQuestion={onUpdateQuestion}
            onApplyVariant={onApplyVariant}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default FormBuilderQuestionCard;
